
import { defineComponent, ref, computed, watch } from 'vue';
import EditTable from './components/edit-table.vue';
import { getCustomerCreditInfo, getDemandSkc, getOrderDetail, updateOrder } from '@/api/production-demand';
import { isMobilePhone } from '@toy/utils';
import { deepClone } from '@/core/utils/index';
import { ElMessage, ElMessageBox } from 'element-plus';
import NP from 'number-precision';
import {
  EMERGENCY_LEVEL_LIST,
  PRODUCT_CHAIN_LIST,
  DELIVERY_TYPE_LIST,
  YES_OR_NO_LIST,
  YES_OR_NO_ENUM,
  YES_OR_NO_NUMBER_ENUM,
  CONTRACT_STATUS_LIST,
} from '@/constant/production-demand/index';
import { useDictionary } from '@/hooks/use-dict';
import AddressPicker from '@/components/address-picker/index.vue';
import { UserReq, UserItem, UserRes } from '@/api/user/index.d';
import { fuzzyQueryUser } from '@/api/user';
import { useFuzzy } from '@/hooks/use-fuzzy';
import { useRoute, useRouter } from 'vue-router';
import {
  IDemandSkcPageListItem,
  IV1ProductionOrderRes,
  IProductionOrderUpdateDetailListItem,
  IProductionOrderUpdateSizeQuantityListItem,
  IV1ProductionOrderDetailListItem,
  ICssCustomerInfoRes,
} from '@/api/production-demand/type';
import { IFile } from '@/components/uploader/packages/types';
import usePermissionConfig from './use-permission-config';
import { cloneDeep } from 'lodash-es';

interface IProductionOrderRes extends IV1ProductionOrderRes {
  _images?: IFile[];
  productChainList?: string[];
}

export default defineComponent({
  components: {
    EditTable,
    AddressPicker,
  },
  emits: ['update:modelValue'],
  setup() {
    const { params } = useRoute();
    const id = params.id as string;

    const router = useRouter();

    const formData = ref({
      demandDetailId: '',
      clothingSizeType: '',
      invoice: '0',
      invoiceTitle: '',
      allowOverflow: '0',
      orderCode: '',
      styleCode: '',
      quantity: '0',
      // 0.2 新增字段
      emergencyLevel: '',
      deliveryType: '',
      // 后端要的字符串
      productChain: '',
      // 前端要数组
      productChainList: [] as string[],
      paymentType: '',
      orderMainstay: '',
      taxPointRate: '',
      // 0.3新增字段
      customerStyleCode: '',
      customerContractCode: '',
      contactName: '',
      contactPhone: '',
      postProvince: '',
      postCity: '',
      postArea: '',
      postAddressDetail: '',
      directorId: '',
      directorCode: '',
      directorName: '',
      merchandiserId: '',
      merchandiserCode: '',
      merchandiserName: '',
      orderAmount: '0',
      serialNumber: '',
      images: [] as string[],
      _images: [] as IFile[],
    } as IProductionOrderRes);

    // 处理生产环节
    watch(() => formData.value.productChainList, (list) => {
      formData.value.productChain = list?.join(',') ?? '';
    });
    // 处理开具发票
    watch(() => formData.value.invoice, (val) => {
      if (val === YES_OR_NO_NUMBER_ENUM.NO) {
        formData.value.invoiceTitle = '';
        formData.value.taxPointRate = '';
      }
    });

    // 默认数据
    const defaultItem = {
      color: '',
      total: '',
      quantity: '',
      deliveryDate: '',
      purchaserBatch: '',
      remark: '',
      confirmPrice: '',
      sizeQuantityList: [
        { size: '', quantity: 0 },
        { size: '', quantity: 0 },
        { size: '', quantity: 0 },
        { size: '', quantity: 0 },
        { size: '', quantity: 0 },
      ],
    };

    // ref
    const formRef = ref();
    const tableRef = ref();
    const spRef = ref();
    const tableData = ref<IProductionOrderUpdateDetailListItem[]>([{ ...defaultItem }]);
    const copyTableData = ref<IProductionOrderUpdateDetailListItem[]>([{ ...defaultItem }]);

    const {
      fuzzyLoading,
      fuzzyResponse,
      fuzzyRemoteMethod,
    } = useFuzzy<UserReq, UserRes>({
      params: { keyword: '' },
      keywordKey: 'keyword',
      API: fuzzyQueryUser,
      beforeResponse(res) {
        return res.data?.map((user: UserItem) => {
          const { username: label, userId: value } = user;
          return { ...user, label, value };
        });
      },
    });

    const init = async () => {
      const res = await getOrderDetail(id);
      tableData.value = res?.data?.detailList.map((item: IV1ProductionOrderDetailListItem) => {
        item.deliveryDate = `${new Date(item.deliveryDate)}`;
        return item;
      }) ?? [];
      formData.value = res.data;
      // 生产环节回显
      formData.value.productChainList = res?.data?.productChain?.split(',') ?? [];
      formData.value._images = res?.data?.images?.map((it: string) => {
        return {
          url: it,
          name: it,
        };
      });
      // 表格数据

      // 默认值
      if (res.data.directorId) {
        fuzzyResponse.value = [{
          value: res.data.directorId,
          label: res.data.directorName,
          userId: res.data.directorId,
          userCode: res.data.directorCode,
          username: res.data.directorName,
        }, {
          value: res.data.merchandiserId,
          label: res.data.merchandiserName,
          userId: res.data.merchandiserId,
          userCode: res.data.merchandiserCode,
          username: res.data.merchandiserName,
        }];
      }
      // // 重置表格校验
      // await nextTick();
      // formRef.value.clearValidate();
    };
    init();

    // 添加尺码
    const handleAddSize = () => {
      tableData.value.forEach((item) => {
        item.sizeQuantityList.push({
          size: '',
          quantity: 0,
        });
      });
    };
    // 移除尺码
    const handleRemoveSize = (index: number) => {
      tableData.value.forEach((item) => {
        item.sizeQuantityList.splice(index, 1);
      });
    };
    // 添加行
    const handleAddRow = () => {
      const list = deepClone(tableData.value);
      const copyList = deepClone(copyTableData.value);
      const sizeQuantityList: IProductionOrderUpdateSizeQuantityListItem[] = [];
      const firstItem = list[0] || copyList[0];
      firstItem?.sizeQuantityList.map((it: IProductionOrderUpdateSizeQuantityListItem) => {
        sizeQuantityList.push({
          size: '',
          quantity: 0,
        });
        return it;
      });
      list.push({
        color: '',
        total: '',
        quantity: '',
        deliveryDate: '',
        remark: '',
        sizeQuantityList,
      } as any);

      tableData.value = list;
    };

    const updateSumQuantity = () => {
      let lists: IProductionOrderUpdateSizeQuantityListItem[] = [];
      tableData.value?.forEach((v1) => {
        lists = lists.concat(v1.sizeQuantityList);
      });

      const sum = lists.reduce((total, cur) => {
        return NP.plus(total, Number(cur.quantity) || 0);
      }, 0);
      formData.value.quantity = String(sum);

      // 计算本单金额
      const orderAmount = tableData.value.reduce((total, item) => {
        const itemCount = item.sizeQuantityList.reduce((t, it) => {
          return NP.plus(t, Number(it.quantity) || 0);
        }, 0);
        return NP.plus(
          total,
          NP.times(itemCount, Number(item.confirmPrice) || 0),
        );
      }, 0);
      formData.value.orderAmount = String(orderAmount);
    };

    // 移除行;
    const handleRemoveRow = (index: number) => {
      tableData.value.splice(index, 1);
      updateSumQuantity();
    };

    const getQuantity = (sizeQuantityList: IProductionOrderUpdateSizeQuantityListItem[]) => {
      let total = 0;
      sizeQuantityList?.forEach((it) => {
        total = NP.plus(total, Number(it.quantity) || 0);
      });
      return total;
    };

    const formatSizeQuantityList = (
      sizeQuantityList: IProductionOrderUpdateSizeQuantityListItem[],
      firstSizeQuantityList: IProductionOrderUpdateSizeQuantityListItem[],
    ) => {
      return sizeQuantityList?.map((it, index) => {
        it.size = firstSizeQuantityList[index].size;
        if (Number(it.quantity) === 0) {
          it.quantity = 0;
        }
        return it;
      });
    };

    const getDetailLists = () => {
      const tableItem = tableData.value[0];
      return tableData.value.map((item) => {
        const quantity = getQuantity(item.sizeQuantityList);
        const _sizeQuantityList = formatSizeQuantityList(item.sizeQuantityList, tableItem.sizeQuantityList);
        return {
          ...item,
          quantity,
          sizeQuantityList: _sizeQuantityList,
          deliveryDate: `${new Date(item.deliveryDate).getTime()}`,
        };
      });
    };

    const verifyRowQuantity = (detailList: any[]) => {
      let isOk = true;
      detailList.forEach((it) => {
        if (Number(it.quantity) === 0) {
          isOk = false;
        }
      });
      return isOk;
    };

    // 生产经理、跟单
    watch(() => formData.value.directorId, (directorId) => {
      if (fuzzyResponse.value.length) {
        const item = fuzzyResponse.value.find(it => it.value === directorId);
        formData.value.directorCode = item?.userCode ?? '';
        formData.value.directorName = item?.username ?? '';
      }
    });
    watch(() => formData.value.merchandiserId, (merchandiserId) => {
      if (fuzzyResponse.value.length) {
        const item = fuzzyResponse.value.find(it => it.value === merchandiserId);
        formData.value.merchandiserCode = item?.userCode ?? '';
        formData.value.merchandiserName = item?.username ?? '';
      }
    });

    // 请求云工厂字典接口，税点、支付方式、接单方式
    const { batchDictListMap } = useDictionary([
      'pims_invoicing_tax_point',
      'pims_payment_method',
      'pims_receiving_entity',
      'plm_standard_size',
    ]);
    // 税点，过滤出已启用的
    const taxPointList = computed(() => {
      return batchDictListMap.value?.pims_invoicing_tax_point?.filter((it) => {
        return it.isEnable === YES_OR_NO_NUMBER_ENUM.YES;
      }) ?? [];
    });
    // 支付方式 取一级
    const paymentMethodList = computed(() => {
      return batchDictListMap.value?.pims_payment_method?.filter((it) => {
        return it.isEnable === YES_OR_NO_NUMBER_ENUM.YES
          && it.valueParentCode === '';
      }) ?? [];
    });
    // 接单方式，取一级
    const receivingEntityList = computed(() => {
      return batchDictListMap.value?.pims_receiving_entity?.filter((it) => {
        return it.isEnable === YES_OR_NO_NUMBER_ENUM.YES
          && it.valueParentCode === '';
      }) ?? [];
    });

    // 当前尺码标准下的尺码列表
    const standardSizeArr = computed(() => {
      return batchDictListMap.value
        ?.plm_standard_size
        ?.find(it => it.valueParentCode === formData.value.clothingSizeType)
        ?.value
        ?.split(',') ?? [];
    });

    const suitableColors = ref<IDemandSkcPageListItem[]>([]);
    watch(() => formData.value.demandDetailId, async (detailId) => {
      const { data } = await getDemandSkc({ demandDetailId: detailId });
      suitableColors.value = data ?? [];
    });

    // 授信查询
    const creditData = ref({} as ICssCustomerInfoRes);
    watch(() => formData.value.purchaserCode, async (val) => {
      if (val) {
        try {
          const { data } = await getCustomerCreditInfo({ customerCode: val });
          creditData.value = data;
          const creditStatus = !!creditData.value?.ccsCustomer;
          !creditStatus && ElMessage.warning('该客户未完成授信，请联系BD在CRM系统完成授信审核！');
        } catch (err) {
          console.log(err);
          creditData.value = {} as ICssCustomerInfoRes;
        }
      }
    });
    // 是否授信客户，同时满足customerStatus=1，amountStatus=1
    const isCreditCustomer = computed(() => {
      return !!creditData.value?.ccsCustomer;
    });

    const handleBack = async () => {
      await ElMessageBox.confirm('是否确定放弃编辑返回？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning',
      });
      router.back();
    };
    const handleSubmit = async () => {
      await formRef.value.validate();
      await tableRef.value.validate();
      await spRef.value.validate();

      const { quantity } = formData.value;

      if (Number(quantity) === 0) {
        ElMessage.warning('单量合计不能为0');
        return;
      }
      const detailList = getDetailLists();
      if (!verifyRowQuantity(detailList)) {
        ElMessage.warning('订单每行合计不能为0');
        return;
      }
      const tableList = cloneDeep(tableData.value).map((item) => {
        item.deliveryDate = `${new Date(item.deliveryDate).getTime()}`;
        return item;
      });
      // 提交
      await updateOrder({
        images: formData.value?._images?.map(it => it.url) ?? [],
        serialNumber: formData.value.serialNumber,
        req: {
          ...formData.value,
          detailList: tableList,
        },
      });
      ElMessage.success('操作成功');
      router.back();
    };

    return {
      fuzzyLoading,
      fuzzyResponse,
      fuzzyRemoteMethod,

      formData,
      // 字典
      // 税点
      taxPointList,
      // 支付方式
      paymentMethodList,
      // 接单方式
      receivingEntityList,

      CONTRACT_STATUS_LIST,
      YES_OR_NO_LIST,
      YES_OR_NO_ENUM,
      YES_OR_NO_NUMBER_ENUM,
      EMERGENCY_LEVEL_LIST,
      PRODUCT_CHAIN_LIST,
      DELIVERY_TYPE_LIST,

      formRef,
      tableRef,
      spRef,

      isCreditCustomer,
      handleBack,
      handleSubmit,

      tableData,
      handleAddSize,
      handleRemoveSize,
      handleAddRow,
      handleRemoveRow,
      updateSumQuantity,
      standardSizeArr,
      suitableColors,
      permissionConfig: usePermissionConfig(),

      rules: {
        emergencyLevel: [{ required: true, message: '请选择紧急程度', trigger: ['change'] }],
        deliveryType: [{ required: true, message: '请选择交付方式', trigger: ['change'] }],
        productChainList: [{ required: true, message: '请选择生产环节', trigger: ['change'] }],
        paymentType: [{ required: true, message: '请选择支付方式', trigger: ['change'] }],
        orderMainstay: [{ required: true, message: '请选择接单主体', trigger: ['change'] }],
        invoiceTitle: [{ required: true, message: '请输入发票抬头', trigger: ['change'] }],
        taxPointRate: [{ required: true, message: '请选择税点', trigger: ['change'] }],
        // 0.3新增
        customerStyleCode: [{ required: true, message: '请输入客户款号', trigger: ['change', 'blur'] }],
        contactName: [{ required: true, message: '请输入联系人', trigger: ['change', 'blur'] }],
        directorId: [{ required: true, message: '请选择生产经理', trigger: ['change', 'blur'] }],
        merchandiserId: [{ required: true, message: '请选择生产跟单员', trigger: ['change', 'blur'] }],
        serialNumber: [{ required: true, message: '请输入飞书审批号', trigger: ['change', 'blur'] }],
        _images: [{ required: true, message: '请上传审批截图', trigger: ['change', 'blur'] }],
        contactPhone: [{
          required: true,
          trigger: ['change', 'blur'],
          validator: (_rule: unknown, value: string, callback: (e?: Error) => void) => {
            if (!isMobilePhone(value)) {
              return callback(new Error('请输入正确的联系手机'));
            }
            return callback();
          },
        }],
        address: [{
          required: true,
          trigger: ['change', 'blur'],
          validator: (_rule: unknown, _value: unknown, callback: (e?: Error) => void) => {
            const { postProvince, postCity, postArea, postAddressDetail } = formData.value;
            if (!postProvince || !postCity || !postArea || !postAddressDetail) {
              return callback(new Error('请选择收货地址'));
            }
            return callback();
          },
        }],
      },
    };
  },
});
